import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { openPaystack } from "./components/paystack";
import ServerWakeOverlay from "./components/ServerWakeOverlay";
import { callServer, wakeServer, startKeepAlive, useServerStatus } from "./serverStatus";
import {
  BASE_URL,
  SUPPORT_EMAIL,
  REGISTRATION_DEADLINE,
  SESSIONS,
  TRACKS,
  PACKAGES,
  BENEFITS,
  formatNaira,
} from "./masterclassConfig";
import { EMPTY_FORM, prepareStorage, loadDraft, saveDraft, clearAll } from "./masterclassStorage";

const isOpen = () => Date.now() < REGISTRATION_DEADLINE.getTime();

// ── Small UI pieces ───────────────────────────────────────────────────────────
const Spinner = ({ label = "Please wait..." }) => (
  <span className="inline-flex items-center justify-center">
    <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
    </svg>
    {label}
  </span>
);

const Card = ({ children, className = "" }) => (
  <div className={`max-w-2xl mx-auto w-full bg-white shadow-md rounded-xl p-5 md:p-8 ${className}`}>{children}</div>
);

const Logo = ({ size = "w-20" }) => (
  <img src="./I2I_logo.png" alt="Innovate to Impact" className={`${size} mx-auto mb-4`} />
);

const Steps = ({ current }) => {
  const steps = ["Your details", "Payment"];
  return (
    <div className="flex items-center justify-center mb-8">
      {steps.map((label, i) => {
        const n = i + 1;
        const active = current >= n;
        return (
          <React.Fragment key={label}>
            <div className="flex flex-col items-center">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm border-2 ${
                  active ? "bg-[#F20034] border-[#F20034] text-white" : "bg-white border-gray-300 text-gray-400"
                }`}
              >
                {current > n ? "✓" : n}
              </div>
              <span className={`mt-1 text-xs font-medium ${active ? "text-[#F20034]" : "text-gray-400"}`}>{label}</span>
            </div>
            {i < steps.length - 1 && (
              <div className={`h-0.5 w-16 sm:w-20 mx-1 mb-4 ${current > n ? "bg-[#F20034]" : "bg-gray-200"}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const SupportLine = () => (
  <p className="text-gray-500 text-sm text-center">
    Questions? Email{" "}
    <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-600 underline break-all">
      {SUPPORT_EMAIL}
    </a>
  </p>
);

// Always-visible way out, shown on every screen after the landing page.
const StartOver = ({ onClick }) => (
  <p className="text-center text-xs text-gray-500 mt-6">
    Having trouble?{" "}
    <button type="button" onClick={onClick} className="underline hover:text-gray-700">
      Start over
    </button>
  </p>
);

// ── Main component ────────────────────────────────────────────────────────────
// Screens: landing → form → payment → success | pending, plus "already" for paid emails.
// Only the typed answers are saved; every visit starts on the landing page.
const MasterclassForm = () => {
  const [step, setStep] = useState("landing");
  const [form, setForm] = useState(() => {
    prepareStorage();
    return loadDraft();
  });
  const [payment, setPayment] = useState(null); // { reference, amountKobo, email } — in memory only
  const [isLoading, setIsLoading] = useState(false);
  const [links, setLinks] = useState(null);

  const server = useServerStatus();

  useEffect(() => saveDraft(form), [form]);

  // Start waking the backend right away so it's ready by the time they submit.
  useEffect(() => {
    wakeServer();
    startKeepAlive();
  }, []);

  // Send people back to the landing page if registration closed while they were mid-way.
  useEffect(() => {
    if (!isOpen() && (step === "form" || step === "payment")) setStep("landing");
  }, [step]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  const selectedPackage = PACKAGES.find((p) => p.id === form.package);
  const selectedTrack = TRACKS.find((t) => t.id === form.track);
  const isFormComplete =
    form.fullName.trim() && form.email.trim() && form.whatsapp.trim() && form.track && form.package;

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  // Saves details on the server and returns a fresh payment reference.
  const register = async () => {
    const { data } = await callServer(
      () =>
        axios.post(`${BASE_URL}/api/masterclass/register`, {
          ...form,
          email: form.email.trim().toLowerCase(),
        }),
      "Saving your registration…"
    );
    const next = { reference: data.reference, amountKobo: data.amountKobo, email: data.email };
    setPayment(next);
    return next;
  };

  const verifyPayment = async (reference) => {
    setIsLoading(true);
    try {
      const { data } = await callServer(
        () => axios.post(`${BASE_URL}/api/masterclass/verify-payment`, { reference }),
        "Confirming your payment…"
      );
      setLinks(data.links || null);
      setStep("success");
      clearAll();
    } catch {
      setStep("pending");
    } finally {
      setIsLoading(false);
    }
  };

  const startPayment = async (pay) => {
    try {
      await openPaystack({
        email: pay.email,
        amountKobo: pay.amountKobo,
        reference: pay.reference,
        metadata: { fullName: form.fullName, track: form.track, package: form.package },
        onSuccess: (res) => verifyPayment(res.reference),
        onClose: () => toast.info("Payment not completed. Tap “Pay” to try again."),
      });
    } catch (err) {
      toast.error(err.message || "Couldn't open the payment window. Please try again.");
    }
  };

  // The server decides where the applicant belongs: already paid → "already" screen,
  // otherwise payment with a fresh reference.
  const registerOrRedirect = async (fallbackMessage) => {
    setIsLoading(true);
    try {
      const pay = await register();
      setStep("payment");
      startPayment(pay);
    } catch (error) {
      if (error.response?.data?.code === "ALREADY_PAID") {
        clearAll();
        setStep("already");
      } else {
        toast.error(error.response?.data?.message || error.message || fallbackMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isFormComplete || isLoading) return;
    registerOrRedirect("Couldn't save your registration. Please try again.");
  };

  // Retrying payment gets a new reference, since Paystack won't reuse one.
  const handleRetryPayment = () => registerOrRedirect("Couldn't start payment. Please try again.");

  const handleResendConfirmation = async () => {
    setIsLoading(true);
    try {
      const { data } = await callServer(
        () =>
          axios.post(`${BASE_URL}/api/masterclass/resend-confirmation`, {
            email: form.email.trim().toLowerCase(),
          }),
        "Sending your access details…"
      );
      toast.success(data.message);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "Couldn't resend right now. Please email us instead.");
    } finally {
      setIsLoading(false);
    }
  };

  const startOver = () => {
    clearAll();
    setForm(EMPTY_FORM);
    setPayment(null);
    setLinks(null);
    setStep("landing");
  };

  const inputClass =
    "w-full p-3 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-300 transition";
  const labelClass = "block text-sm font-semibold text-gray-800";
  const hintClass = "text-xs text-gray-500 mt-0.5";

  const open = isOpen();

  return (
    <div className="bg-gray-100 min-h-screen py-0 md:py-8 px-0 md:px-4">
      <ServerWakeOverlay />
      {/* ══ Landing ═════════════════════════════════════════════════════════ */}
      {step === "landing" && (
        <div className="max-w-4xl mx-auto w-full bg-white shadow-md md:rounded-xl overflow-hidden">
          <div className="px-5 md:px-10 pt-8 pb-10 text-center bg-gradient-to-b from-red-50 to-white">
            <Logo size="w-28" />
            <p className="text-xs font-semibold tracking-widest text-[#F20034] uppercase mb-3">
              Innovate to Impact · AI Masterclass
            </p>
            <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-4">
              Catch the AI Train: Ride the Future of Work &amp; Income Generation
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto mb-6">
              AI adoption is accelerating globally. Don't get left behind. This subsidised, hands-on intensive gives
              you practical skills you can turn into a source of income straight away.
            </p>

            <div className="flex flex-wrap justify-center gap-2 text-sm text-gray-700 mb-8">
              <span className="bg-white border rounded-full px-3 py-1">📅 Nov 20 – 21, 2026</span>
              <span className="bg-white border rounded-full px-3 py-1">💻 100% virtual · Google Meet</span>
              <span className="bg-white border rounded-full px-3 py-1">⏱️ 6 hours over 2 days</span>
            </div>

            {open ? (
              <>
                <button
                  onClick={() => setStep("form")}
                  className="px-8 py-3 bg-[#F20034] text-white font-semibold rounded-lg hover:bg-red-700 transition"
                >
                  Register now — from {formatNaira(PACKAGES[0].amountKobo)}
                </button>
                <p className="text-xs text-gray-500 mt-3">
                  Registration closes Friday, Nov 20 at 6 PM WAT, right before kickoff.
                </p>
                {server.status === "waking" && (
                  <p className="text-xs text-gray-400 mt-2 inline-flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" /> Preparing registration…
                  </p>
                )}
              </>
            ) : (
              <div className="inline-block bg-gray-100 border rounded-lg px-5 py-3 text-gray-700">
                Registration for this masterclass has closed.
              </div>
            )}
          </div>

          <div className="px-5 md:px-10 pb-10 space-y-10">
            {/* Schedule */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">Schedule</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {SESSIONS.map((s) => (
                  <div key={s.day} className="border rounded-lg p-4">
                    <p className="text-xs font-semibold text-[#F20034] uppercase">{s.day}</p>
                    <p className="font-semibold text-gray-900">{s.date}</p>
                    <p className="text-gray-600 text-sm">{s.time}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Tracks */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-1">Choose your track</h2>
              <p className="text-sm text-gray-500 mb-3">You'll pick one when you register.</p>
              <div className="grid md:grid-cols-3 gap-3">
                {TRACKS.map((t) => (
                  <div key={t.id} className="border rounded-lg p-4">
                    <div className="text-2xl mb-2">{t.icon}</div>
                    <p className="font-semibold text-gray-900">{t.name}</p>
                    <p className="text-sm text-gray-600 mt-1">{t.blurb}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Benefits */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">What you get</h2>
              <ul className="grid md:grid-cols-3 gap-3">
                {BENEFITS.map((b) => (
                  <li key={b.title} className="bg-gray-50 rounded-lg p-4">
                    <p className="font-semibold text-gray-900">✓ {b.title}</p>
                    <p className="text-sm text-gray-600 mt-1">{b.text}</p>
                  </li>
                ))}
              </ul>
            </section>

            {/* Packages */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">Access packages</h2>
              <div className="grid md:grid-cols-2 gap-3">
                {PACKAGES.map((p) => (
                  <div
                    key={p.id}
                    className={`rounded-lg p-5 border-2 ${p.highlight ? "border-[#F20034]" : "border-gray-200"}`}
                  >
                    {p.highlight && (
                      <span className="text-xs font-semibold text-white bg-[#F20034] rounded-full px-2 py-0.5">
                        Best value
                      </span>
                    )}
                    <p className="font-semibold text-gray-900 mt-2">{p.name}</p>
                    <p className="text-2xl font-extrabold text-gray-900 my-1">{formatNaira(p.amountKobo)}</p>
                    <p className="text-sm text-gray-600">{p.blurb}</p>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-600 mt-3">
                <strong>Registering a team?</strong> Corporate and group rates are available.{" "}
                <a
                  href={`mailto:${SUPPORT_EMAIL}?subject=AI%20Masterclass%20group%20rate`}
                  className="text-blue-600 underline"
                >
                  Email us
                </a>
                .
              </p>
            </section>

            {/* How it works */}
            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">How to register</h2>
              <ol className="list-decimal list-inside text-gray-700 space-y-1">
                <li>Fill in your details and choose your track and package.</li>
                <li>Pay securely with Paystack right after you submit.</li>
                <li>Get your Google Meet link, pre-reading guide, and WhatsApp group link by email once payment is confirmed.</li>
              </ol>
            </section>

            {open && (
              <div className="text-center">
                <button
                  onClick={() => setStep("form")}
                  className="px-8 py-3 bg-[#F20034] text-white font-semibold rounded-lg hover:bg-red-700 transition"
                >
                  👉 Register now
                </button>
              </div>
            )}

            <SupportLine />
          </div>
        </div>
      )}

      {/* ══ Form ════════════════════════════════════════════════════════════ */}
      {step === "form" && (
        <Card>
          <Logo />
          <h1 className="text-2xl font-bold text-center text-gray-900 mb-1">AI Masterclass registration</h1>
          <p className="text-center text-gray-500 text-sm mb-6">Nov 20 – 21, 2026 · Google Meet</p>
          <Steps current={1} />

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className={labelClass} htmlFor="fullName">Full name</label>
              <p className={hintClass}>As you want it to appear on your certificate of completion.</p>
              <input
                id="fullName" type="text" autoComplete="name" value={form.fullName}
                onChange={update("fullName")} className={inputClass} placeholder="Ada Okonkwo"
              />
            </div>

            <div>
              <label className={labelClass} htmlFor="email">Email address</label>
              <p className={hintClass}>Your Google Meet links and onboarding materials will be sent here.</p>
              <input
                id="email" type="email" autoComplete="email" value={form.email}
                onChange={update("email")} className={inputClass} placeholder="you@example.com"
              />
            </div>

            <div>
              <label className={labelClass} htmlFor="whatsapp">WhatsApp phone number</label>
              <p className={hintClass}>Include your country code (e.g. +234…). We'll use it to add you to the support group.</p>
              <input
                id="whatsapp" type="tel" autoComplete="tel" value={form.whatsapp}
                onChange={update("whatsapp")} className={inputClass} placeholder="+234 801 234 5678"
              />
            </div>

            <fieldset>
              <legend className={labelClass}>Select your learning track</legend>
              <div className="grid gap-2 mt-2">
                {TRACKS.map((t) => (
                  <label
                    key={t.id}
                    className={`flex items-start gap-3 border-2 rounded-lg p-3 cursor-pointer transition ${
                      form.track === t.id ? "border-[#F20034] bg-red-50" : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <input
                      type="radio" name="track" value={t.id} checked={form.track === t.id}
                      onChange={update("track")} className="mt-1 accent-[#F20034]"
                    />
                    <span>
                      <span className="font-semibold text-gray-900">{t.icon} {t.name}</span>
                      <span className="block text-sm text-gray-600">{t.short}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            <fieldset>
              <legend className={labelClass}>Select your access package</legend>
              <div className="grid gap-2 mt-2">
                {PACKAGES.map((p) => (
                  <label
                    key={p.id}
                    className={`flex items-start gap-3 border-2 rounded-lg p-3 cursor-pointer transition ${
                      form.package === p.id ? "border-[#F20034] bg-red-50" : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <input
                      type="radio" name="package" value={p.id} checked={form.package === p.id}
                      onChange={update("package")} className="mt-1 accent-[#F20034]"
                    />
                    <span className="flex-1">
                      <span className="flex flex-wrap justify-between gap-x-2">
                        <span className="font-semibold text-gray-900">{p.name}</span>
                        <span className="font-bold text-gray-900">{formatNaira(p.amountKobo)}</span>
                      </span>
                      <span className="block text-sm text-gray-600">{p.blurb}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            <button
              type="submit"
              disabled={!isFormComplete || isLoading}
              className={`w-full p-3 rounded-lg font-semibold transition ${
                !isFormComplete || isLoading
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-[#F20034] text-white hover:bg-red-700"
              }`}
            >
              {isLoading ? (
                <Spinner label="Saving..." />
              ) : selectedPackage ? (
                `Continue to payment · ${formatNaira(selectedPackage.amountKobo)}`
              ) : (
                "Continue to payment"
              )}
            </button>

            <button type="button" onClick={() => setStep("landing")} className="w-full text-sm text-gray-500 underline">
              ← Back to masterclass details
            </button>
          </form>
          <StartOver onClick={startOver} />
        </Card>
      )}

      {/* ══ Payment ═════════════════════════════════════════════════════════ */}
      {step === "payment" && (
        <Card>
          <Logo />
          <h1 className="text-2xl font-bold text-center text-gray-900 mb-6">Complete your registration</h1>
          <Steps current={2} />

          <div className="bg-gray-50 border rounded-lg p-4 mb-5 text-sm space-y-2">
            <div className="flex justify-between gap-4"><span className="text-gray-600">Name</span><span className="font-medium text-right">{form.fullName}</span></div>
            <div className="flex justify-between gap-4"><span className="text-gray-600">Email</span><span className="font-medium text-right break-all">{form.email}</span></div>
            <div className="flex justify-between gap-4"><span className="text-gray-600">Track</span><span className="font-medium text-right">{selectedTrack?.name}</span></div>
            <div className="flex justify-between gap-4"><span className="text-gray-600">Package</span><span className="font-medium text-right">{selectedPackage?.name}</span></div>
            <hr />
            <div className="flex justify-between text-base">
              <span className="font-bold">Total</span>
              <span className="font-bold">{payment ? formatNaira(payment.amountKobo) : "—"}</span>
            </div>
          </div>

          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4 mb-5">
            <span className="text-lg">⚠️</span>
            <p className="text-amber-800 text-sm">
              <strong>Your seat isn't confirmed until payment is complete.</strong> Please keep this page open until you
              see the success screen.
            </p>
          </div>

          <button
            onClick={handleRetryPayment}
            disabled={isLoading}
            className={`w-full p-3 rounded-lg font-semibold transition ${
              isLoading ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-[#F20034] text-white hover:bg-red-700"
            }`}
          >
            {isLoading ? <Spinner label="Confirming..." /> : `Pay ${payment ? formatNaira(payment.amountKobo) : ""} with Paystack`}
          </button>

          <button onClick={() => setStep("form")} className="w-full text-sm text-gray-500 underline mt-3">
            ← Edit my details
          </button>
          <p className="text-center text-xs text-gray-400 mt-4">Secured by Paystack</p>
          <StartOver onClick={startOver} />
        </Card>
      )}

      {/* ══ Already registered and paid ═════════════════════════════════════ */}
      {step === "already" && (
        <Card className="max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5 text-3xl">✓</div>
          <h1 className="text-2xl font-bold text-green-700 mb-2">You're already registered</h1>
          <p className="text-gray-700 mb-4">
            <strong className="break-all">{form.email}</strong> has already paid for the AI Masterclass. You don't
            need to pay again.
          </p>
          <p className="text-gray-600 text-sm mb-6">
            Your access details were emailed to you. Can't find them? Check your spam folder, or we can send them again.
          </p>
          <button
            onClick={handleResendConfirmation}
            disabled={isLoading}
            className={`w-full p-3 rounded-lg font-semibold transition mb-5 ${
              isLoading ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-[#F20034] text-white hover:bg-red-700"
            }`}
          >
            {isLoading ? <Spinner label="Sending..." /> : "Resend my access details"}
          </button>
          <SupportLine />
          <StartOver onClick={startOver} />
        </Card>
      )}

      {/* ══ Success ═════════════════════════════════════════════════════════ */}
      {step === "success" && (
        <Card className="max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5 text-3xl">✓</div>
          <h1 className="text-2xl font-bold text-green-700 mb-2">You're in! 🎉</h1>
          <p className="text-gray-700 mb-4">Your payment is confirmed and your seat at the AI Masterclass is secured.</p>
          <p className="text-gray-600 text-sm mb-6">
            Your access details are on their way to your inbox. Check your spam folder if you don't see them within a
            few minutes.
          </p>

          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-gray-700 mb-5 space-y-1">
            {SESSIONS.map((s) => (
              <p key={s.day}><strong>{s.date}</strong> · {s.time}</p>
            ))}
            {links?.whatsapp && (
              <a
                href={links.whatsapp} target="_blank" rel="noopener noreferrer"
                className="inline-block mt-3 px-4 py-2 bg-green-600 text-white rounded-md font-semibold hover:bg-green-700"
              >
                Join the WhatsApp support group →
              </a>
            )}
          </div>
          <SupportLine />
        </Card>
      )}

      {/* ══ Payment pending ════════════════════════════════════════════════ */}
      {step === "pending" && (
        <Card className="max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-5 text-3xl">⏳</div>
          <h1 className="text-2xl font-bold text-amber-600 mb-2">Payment processing</h1>
          <p className="text-gray-700 mb-4">We're still confirming your payment. This usually takes a few minutes.</p>
          <p className="text-gray-600 text-sm mb-6">
            If your payment went through, you'll get a confirmation email with your access links shortly. No need to pay
            again.
          </p>
          <p className="text-gray-600 text-sm mb-6">
            Nothing after 30 minutes? Email us with your payment reference
            {payment?.reference ? (
              <> (<code className="bg-gray-100 px-1 rounded">{payment.reference}</code>)</>
            ) : null}
            .
          </p>
          <SupportLine />
          <StartOver onClick={startOver} />
        </Card>
      )}
    </div>
  );
};

export default MasterclassForm;
